import { useCallback, useEffect, useRef, useState } from "react";
import type { CurrentLICPacket } from "@/api/model/current-lic-packet";
import type { DeviceMessageRequest } from "@/api/model/device-message-request";
import { loadCurrentLICPacketForDeviceAndType } from "@/api/queries/current-lic-packet";
import { apiService } from "@/api/service";

export type LICCommunicationTestStatus = "idle" | "created" | "pending" | "sent" | "failed" | "acknowledged";

export interface LICCommunicationTestError {
  phase: "request_creation" | "request_polling" | "packet_polling";
  message: string;
  originalError?: any;
}

export interface LICCommunicationTestParams {
  deviceId: string;
  pollingInterval?: number;
  timeout?: number;
}

export interface LICCommunicationTestResult {
  status: LICCommunicationTestStatus;
  error: LICCommunicationTestError | null;
  deviceMessageRequest: DeviceMessageRequest | null;
  currentPacket: CurrentLICPacket | null;
  isPolling: boolean;
  TesteIniciar: () => Promise<void>;
}

export function useLICDepurationData({
  deviceId,
  pollingInterval = 2000,
  timeout = 30000,
}: LICCommunicationTestParams): LICCommunicationTestResult & { start: () => Promise<void> } {
  const [status, setStatus] = useState<LICCommunicationTestStatus>("idle");
  const [error, setError] = useState<LICCommunicationTestError | null>(null);
  const [deviceMessageRequest, setDeviceMessageRequest] = useState<DeviceMessageRequest | null>(null);
  const [currentPacket, setCurrentPacket] = useState<CurrentLICPacket | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const pollingRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const abortRef = useRef(false);

  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = undefined;
    }
    setIsPolling(false);
    abortRef.current = true;
  }, []);

  const pollDeviceMessageRequest = useCallback(
    async (id: string) => {
      const poll = async () => {
        if (abortRef.current) return;

        try {
          const updatedRequest = await apiService.deviceMessageRequest.getOne(id);

          if (abortRef.current) return;

          setDeviceMessageRequest(updatedRequest);

          switch (updatedRequest.status) {
            case "pending":
            case "processing":
              setStatus("pending");
              pollingRef.current = setTimeout(poll, pollingInterval);
              break;

            case "failed":
            case "expired":
            case "cancelled":
              setError({
                phase: "request_polling",
                message: `Device message request ${updatedRequest.status}`,
              });
              setStatus("failed");
              cleanup();
              break;

            case "acknowledged":
              setStatus("acknowledged");
              cleanup();
              break;

            case "sent":
              setStatus("sent");
              await pollCurrentLICPacket(updatedRequest);
              break;

            default:
              pollingRef.current = setTimeout(poll, pollingInterval);
          }
        } catch (err) {
          if (!abortRef.current) {
            setError({
              phase: "request_polling",
              message: "Failed to poll device message request",
              originalError: err,
            });
            setStatus("failed");
            cleanup();
          }
        }
      };

      await poll();
    },
    [pollingInterval, cleanup]
  );

  const pollCurrentLICPacket = useCallback(
  async (messageRequest: DeviceMessageRequest) => {
    const poll = async () => {
      if (abortRef.current) return;

      try {
        const packet = await loadCurrentLICPacketForDeviceAndType(apiService, deviceId, "info");

        if (abortRef.current) return;

        console.log("pollCurrentLICPacket: Pacote encontrado", packet);
        console.log("packet.packet_date:", packet?.packet_date);
        console.log("messageRequest.date_created:", messageRequest?.date_created);

        // MODIFICAÇÃO AQUI: Aceita o pacote se ele existir, independente da data
        if (packet) {
          setCurrentPacket(packet);
          setStatus("acknowledged");
          cleanup();
        } else {
          // Continua polling
          pollingRef.current = setTimeout(poll, pollingInterval);
        }
      } catch (err) {
        if (!abortRef.current) {
          setError({
            phase: "packet_polling",
            message: "Failed to poll current LIC packet",
            originalError: err,
          });
          setStatus("failed");
          cleanup();
        }
      }
    };

    await poll();
  },
  [deviceId, pollingInterval, cleanup]
);

  const TesteIniciar = async () => {
  setIsPolling(true);
  setStatus("pending");
  
  try {
    console.log("TesteIniciar: Buscando pacote INFO para device", deviceId);
    
    const packet = await loadCurrentLICPacketForDeviceAndType(apiService, deviceId, "info");
    
    console.log("TesteIniciar: Pacote recebido", packet);
    
    if (packet) {
      setCurrentPacket(packet);
      setStatus("acknowledged");
    } else {
      console.log("TesteIniciar: Nenhum pacote encontrado, criando solicitação...");
      await start(); 
    }
  } catch (err) {
    console.error("TesteIniciar: Erro", err);
    if (!abortRef.current) {
      setError({
        phase: "packet_polling",
        message: "Failed to poll current LIC packet",
        originalError: err,
      });
      setStatus("failed");
    }
  } finally {
    setIsPolling(false);
  }
}

  const start = useCallback(async () => {
    abortRef.current = false;
    setStatus("created");
    setError(null);
    setCurrentPacket(null);

    try {
      
        const created = await apiService.deviceMessageRequest.create({
        device: deviceId,
        payload_type: "request_info",
        payload_data: {},
        scheduled_at: new Date().toISOString(),
        priority: 1,
        max_attempts: 1,
      });

      setDeviceMessageRequest(created);
      setStatus("created");

      // inicia polling
      await pollDeviceMessageRequest(created.id);
    } catch (err) {
      setError({
        phase: "request_creation",
        message: "Failed to create device message request",
        originalError: err,
      });
      setStatus("failed");
    }
  }, [deviceId, pollDeviceMessageRequest]);

  // cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    status,
    error,
    deviceMessageRequest,
    currentPacket,
    isPolling,
    start,
    TesteIniciar
  };
}
